<template>
  <v-dialog v-model="showDialog" max-width="1080" :fullscreen="isPhoneBool">
    <v-card v-if="loading || error" class="job-dialog-card loading-card">
      <CenteredLoading :displayBool="true" :completedBool="!loading" :successBool="!error" :displayText="''">
        <!-- $t('text.loading') -->
      </CenteredLoading>
    </v-card>

    <v-card v-else-if="Job" class="job-dialog-card">
      <div class="d-flex align-start justify-space-between pa-4">
        <div class="min-width-0">
          <div class="d-flex align-center flex-wrap ga-2">
            <h2 class="ma-0">{{ displayOrderNumber }}</h2>
          </div>
        </div>

        <v-btn size="small" color="error" icon @click="hide()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-divider />

      <v-card-text class="dialog-body">
        <v-row>
          <v-col cols="12" md="4">
            <v-card>
              <v-card-text class="pa-3">
                <div class="d-flex align-center ga-2 text-medium-emphasis">
                  <v-icon size="18" color="primary">mdi-clipboard-text-outline</v-icon>
                  <span class="text-caption font-weight-bold text-uppercase">{{ $t('text.ordernumber') }}</span>
                </div>
                <v-sheet color="transparent" class="d-flex justify-space-between align-center py-1">
                  <span class="text-body-2 text-medium-emphasis">{{ $t('text.ordernumber') }}</span>
                  <strong class="text-body-2 text-right">{{ displayOrderNumber }}</strong>
                </v-sheet>
                <v-sheet color="transparent" class="d-flex justify-space-between align-center py-1">
                  <span class="text-body-2 text-medium-emphasis">{{ $t('text.type') }}</span>
                  <JobTypeChip :typeId="Job.JobTypeId" :typeName="Job.JobTypeName" />
                </v-sheet>
                <v-sheet color="transparent" class="d-flex justify-space-between align-center py-1">
                  <span class="text-body-2 text-medium-emphasis">{{ $t('text.status') }}</span>
                  <JobStatusChip :statusId="Job.StatusId" :statusName="Job.StatusName" />
                </v-sheet>
                <v-sheet color="transparent" class="d-flex justify-space-between align-center py-1">
                  <span class="text-body-2 text-medium-emphasis">{{ $t('text.created_date') }}</span>
                  <strong class="text-body-2 text-right">{{ formatDateTime(Job.CreatedDate) }}</strong>
                </v-sheet>
                <v-sheet color="transparent" class="d-flex justify-space-between align-center py-1">
                  <span class="text-body-2 text-medium-emphasis">{{ $t('text.priority') }}</span>
                  <strong class="text-body-2 text-right">{{ Job.JobPriority.Name }}</strong>
                </v-sheet>
                <v-sheet v-if="Job.EstimatedTime" color="transparent"
                  class="d-flex justify-space-between align-center py-1">
                  <span class="text-body-2 text-medium-emphasis">{{ $t('text.estimated_time') }}</span>
                  <strong class="text-body-2 text-right">{{ formatHours(Job.EstimatedTime) }}</strong>
                </v-sheet>
              </v-card-text>
            </v-card>

            <v-card class="mt-3">
              <v-card-text class="pa-3">
                <div class="d-flex align-center ga-2 mb-3 text-medium-emphasis">
                  <v-icon size="18" color="primary">mdi-calendar-clock</v-icon>
                  <span class="text-caption font-weight-bold text-uppercase">{{ $t('text.scheduledfor') }}</span>
                </div>

                <div v-if="appointments.length" class="appointment-list">
                  <v-sheet v-for="appointment in appointments"
                    :key="appointment.Id || appointment.StartTime || appointment.AppointmentDate" rounded="lg"
                    class="pa-3 border-sm bg-grey-lighten-5">
                    <div class="text-body-2 font-weight-bold">
                      {{ formatDate(appointment.AppointmentDate || appointment.StartTime) }}
                    </div>
                    <div class="text-caption text-medium-emphasis">
                      {{ formatAppointmentTime(appointment) }}
                    </div>
                    <div v-if="appointment.Subject" class="text-caption text-medium-emphasis mt-1">
                      {{ appointment.Subject }}
                    </div>
                  </v-sheet>
                </div>
                <div v-else class="text-body-2 text-medium-emphasis">
                  {{ $t('text.not_scheduled') }}
                </div>
              </v-card-text>
            </v-card>

            <v-card v-if="hasAddress" class="mt-3">
              <v-card-text class="pa-3">
                <div class="d-flex align-center ga-2 mb-3 text-medium-emphasis">
                  <v-icon size="18" color="primary">mdi-map-marker-outline</v-icon>
                  <span class="text-caption font-weight-bold text-uppercase">{{ $t('text.delivery_address') }}</span>
                </div>
                <div class="text-body-2 text-medium-emphasis">
                  <div v-for="line in addressLines" :key="line">{{ line }}</div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="8">
            <v-card>
              <v-card-text class="pa-3">
                <div class="d-flex align-center justify-space-between mb-3">
                  <div class="d-flex align-center ga-2 text-medium-emphasis">
                    <v-icon size="18" color="primary">mdi-devices</v-icon>
                    <span class="text-caption font-weight-bold text-uppercase">{{ $t('text.Machines') }}</span>
                  </div>
                  <v-chip v-if="childJobs.length" size="small">{{ childJobs.length }}</v-chip>
                </div>

                <div v-if="childJobs.length" class="machine-list">
                  <JobDialogMachineItem v-for="machineJob in childJobs" :key="machineJob.Id" :machine-job="machineJob"
                    @open="openMachine">
                  </JobDialogMachineItem>
                </div>

                <v-sheet v-else rounded class="pa-6 text-center text-body-2 text-medium-emphasis">
                  {{ $t('text.Machines') }}: 0
                </v-sheet>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions class="px-4 py-3">
        <v-spacer />
        <v-btn @click="hide" variant="outlined" color="accent">
          {{ $t('text.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>


<script>
import http from "@/api/client";
import JobDialogMachineItem from "@/components/Dialogs/JobDialogMachineItem.vue";
import JobStatusChip from '@/components/JobStatusChip.vue';
import JobTypeChip from '@/components/JobTypeChip.vue';
import CenteredLoading from "@/components/CenteredLoading.vue";

export default {
  name: "JobDialog",
  components: {
    JobDialogMachineItem,
    JobStatusChip,
    JobTypeChip,
    CenteredLoading,
  },

  data() {
    return {
      showDialog: false,
      loading: false,
      error: null,
      Job: null,
    };
  },

  mounted() {
    this.init();
  },

  methods: {
    async init() {
      var jobId = this.$route.params.jobId;
      if (jobId) {
        this.showDialog = true;
        this.loading = true;
        this.error = null;
        await http()
          .get("api2/customer/jobs/" + jobId)
          .then((response) => {
            this.Job = response.data;
          })
          .catch((e) => {
            this.error = e;
          });
        this.loading = false;
      }
    },

    openMachine(machine) {
      if (!machine) return;
      this.$router.push({ name: 'ViewMachine', params: { id: machine.Id } });
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    formatDateTime(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatTime(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatAppointmentTime(appointment) {
      if (appointment.StartTime && appointment.EndTime) {
        return this.formatTime(appointment.StartTime) + " - " + this.formatTime(appointment.EndTime);
      }
      return this.formatDateTime(appointment.AppointmentDate || appointment.StartTime || appointment.EndTime);
    },

    formatHours(hours) {
      if (!hours) return "";
      return hours + " " + this.$t("text.hours");
    },

    getAddressLines(source) {
      if (!source) return [];
      return [
        source.CustomerDeliveryAdressName,
        source.CustomerDeliveryAdressStreet,
        [source.CustomerDeliveryAdressZip, source.CustomerDeliveryAdressCity].filter(Boolean).join(" "),
        source.CustomerDeliveryAdressCountry,
      ].filter(Boolean);
    },

    hide() {
      if (!this.$route.params.jobId) {
        this.showDialog = false;
        return;
      }

      var currentParams = Object.assign({}, this.$route.params);
      currentParams.jobId = "";
      this.$router.push({
        name: this.$route.name,
        params: currentParams,
        query: this.$route.query,
        hash: this.$route.hash,
      });
    },
  },

  computed: {
    displayOrderNumber() {
      if (!this.Job) return "";
      return this.Job.JobTypeId != 3 ? this.Job.OrderId : "S" + this.Job.Id;
    },

    appointments() {
      return this.Job && this.Job.Appointments ? this.Job.Appointments : [];
    },

    childJobs() {
      return this.Job && this.Job.InverseParent ? this.Job.InverseParent : [];
    },

    addressLines() {
      return this.getAddressLines(this.Job);
    },

    hasAddress() {
      return this.addressLines.length > 0;
    },
  },

  watch: {
    "$route.params.jobId"(val) {
      val ? this.init() : (this.showDialog = false);
    },

    showDialog: function (val) {
      if (!val && this.$route.params.jobId) {
        this.hide();
      }
    },
  },
};
</script>

<style scoped>
.job-dialog-card {
  border-radius: 8px;
  overflow: hidden;
}

.loading-card {
  min-height: 75vh;
}

.dialog-body {
  background: #f7f8fa;
  max-height: 72vh;
  overflow-y: auto;
  padding: 16px;
}

.appointment-list,
.machine-list {
  display: grid;
  gap: 10px;
}

.machine-list {
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 2px;
}

@media (max-width: 600px) {
  .dialog-body {
    max-height: none;
    padding: 12px;
  }

  .machine-list {
    max-height: none;
  }
}
</style>